import { useEffect, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMe } from "@/features/auth/useMe";
import { useAuthStore } from "@/stores/auth";
import { buildAuth0LoginPath } from "@/features/auth/authPaths";
import { getAuthMode } from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import type { MePayload } from "@/lib/types";

/**
 * `/hub` — role-based redirect (M12a).
 *
 * Priority order:
 *   - global admin / env-admin / team_admin / uploader → `/admin`
 *   - coach / assistant_coach                          → `/coach`
 *   - signed-in user with no team/membership           → `/register`
 *   - guardian / player / viewer-only with memberships → `/feedback`
 *   - anonymous, Auth0 enabled           → `/auth/login?next=/hub`
 *   - anonymous, legacy-only/error       → `/login?next=/hub`
 *
 * The Hub button on the marketing landing reads "tktic hub" — the
 * role-based dispatcher, not a fixed coach-review shortcut. Admins
 * land in the admin console, coaches land in the coach workspace,
 * and viewers / guardians / players go to `/feedback`.
 *
 * `useRef` guards the redirect so TanStack Router's parent-stays-mounted
 * behavior across an intra-effect navigate doesn't fire the redirect a
 * second time (same load-bearing pattern as M9a / M9b auth gates).
 */
export function HubRedirect() {
  const navigate = useNavigate();
  const meQuery = useMe();
  const authMe = useAuthStore((s) => s.me);
  const hydrating = useAuthStore((s) => s.hydrating);
  const authMode = useQuery({
    queryKey: unscopedKey("auth-mode"),
    queryFn: getAuthMode,
    staleTime: 60_000,
  });
  const redirected = useRef(false);

  useEffect(() => {
    if (redirected.current) return;
    if (hydrating || meQuery.isLoading || authMode.isPending) return;

    const user = authMe?.user;
    if (!user) {
      redirected.current = true;
      if (authMode.data?.auth0_enabled === true) {
        window.location.replace(buildAuth0LoginPath("/hub"));
        return;
      }
      void navigate({
        to: "/login",
        search: { next: "/hub" },
        replace: true,
      });
      return;
    }

    const landing = getHubLanding(authMe);

    redirected.current = true;
    if (landing) {
      void navigate({ to: landing, replace: true });
    } else {
      void navigate({ to: "/feedback", replace: true });
    }
  }, [
    authMe,
    authMode.data,
    authMode.isPending,
    hydrating,
    meQuery.isLoading,
    navigate,
  ]);

  return (
    <main
      data-testid="hub-redirect"
      className="flex min-h-[40vh] items-center justify-center bg-canvas text-ink"
    >
      <p className="font-mono text-sm text-ink-muted">Opening your hub…</p>
    </main>
  );
}

export type HubLandingPath = "/admin" | "/coach" | "/feedback" | "/register";

export function getHubLanding(
  me: MePayload | null | undefined,
): HubLandingPath | null {
  const user = me?.user;
  if (!user) return null;

  const roles = user.roles ?? [];
  const memberships = me?.memberships ?? [];
  const isGlobalAdmin = !!user.is_global_admin || roles.includes("admin");
  const isUploader = roles.includes("uploader");
  const isTeamAdmin = memberships.some((m) => m.role === "team_admin");
  const isCoach = memberships.some(
    (m) => m.role === "coach" || m.role === "assistant_coach",
  );

  if (isGlobalAdmin || isTeamAdmin || isUploader) return "/admin";
  if (isCoach) return "/coach";
  if (memberships.length === 0) return "/register";
  return "/feedback";
}
